'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useBuilderStore } from '@/stores'
import { useStationeryBuilderStore } from '@/stores/stationeryBuilder'

/**
 * Clears builder state (both localStorage AND Zustand store) when navigating AWAY from builders.
 * Must clear both because the store is a singleton - in-memory data would re-persist otherwise.
 *
 * Handles both builders:
 * - Digital builder: /builder → clears dday-builder-storage
 * - Stationery builder: /stationery-builder → clears dday-stationery-builder-storage
 *
 * Also ensures cleanup on direct navigation (e.g., page refresh on non-builder page).
 */
export function BuilderStorageCleanup() {
  const pathname = usePathname()
  const clearDigitalBuilder = useBuilderStore((state) => state.clearBuilder)
  const clearStationeryBuilder = useStationeryBuilderStore(
    (state) => state.clearBuilder
  )
  const previousPathnameRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip if pathname is not available (edge case during transitions)
    if (!pathname) return

    // Determine which builder pages we're on/were on
    // Use startsWith to avoid /stationery-builder matching /builder
    const isOnDigitalBuilder =
      pathname.startsWith('/builder') ||
      pathname.startsWith('/en/builder') ||
      pathname.startsWith('/ko/builder')
    const isOnStationeryBuilder =
      pathname.startsWith('/stationery-builder') ||
      pathname.startsWith('/en/stationery-builder') ||
      pathname.startsWith('/ko/stationery-builder')
    const isOnAuthRoute =
      pathname.includes('/login') || pathname.includes('/signup')

    const wasOnDigitalBuilder =
      previousPathnameRef.current?.startsWith('/builder') ||
      previousPathnameRef.current?.startsWith('/en/builder') ||
      previousPathnameRef.current?.startsWith('/ko/builder')
    const wasOnStationeryBuilder =
      previousPathnameRef.current?.startsWith('/stationery-builder') ||
      previousPathnameRef.current?.startsWith('/en/stationery-builder') ||
      previousPathnameRef.current?.startsWith('/ko/stationery-builder')

    // Preserve digital builder state on auth routes so post-login image transfer
    // can reuse the same preassigned invitationId from the guest builder session.
    if (
      (wasOnDigitalBuilder && !isOnDigitalBuilder && !isOnAuthRoute) ||
      (!isOnDigitalBuilder &&
        !isOnAuthRoute &&
        localStorage.getItem('dday-builder-storage'))
    ) {
      const hasLocalStorage = localStorage.getItem('dday-builder-storage')
      const hasStoreData = useBuilderStore.getState().invitationData.id

      if (hasLocalStorage || hasStoreData) {
        clearDigitalBuilder()
      }
    }

    // Clear stationery builder when navigating away from it OR when on non-builder page with stale storage
    if (
      (wasOnStationeryBuilder && !isOnStationeryBuilder) ||
      (!isOnStationeryBuilder &&
        localStorage.getItem('dday-stationery-builder-storage'))
    ) {
      const hasLocalStorage = localStorage.getItem(
        'dday-stationery-builder-storage'
      )
      const hasStoreData = useStationeryBuilderStore.getState().stationeryId

      if (hasLocalStorage || hasStoreData) {
        clearStationeryBuilder()
      }
    }

    // Update previous pathname for next render
    previousPathnameRef.current = pathname
  }, [pathname, clearDigitalBuilder, clearStationeryBuilder])

  return null
}
